import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Documentation } from 'graphql-common';
import {
  useEdmDocumentationBulkFavoriteMutation,
  useEdmDocumentationBulkUnfavoriteMutation,
} from '@lib/graphql-common';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { APP_URLS } from 'constants/urls';

export default function useDocumentItemActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [bulkFavorite] = useEdmDocumentationBulkFavoriteMutation({
    onError: (error) => showGraphQLErrors(error, t),
  });
  const [bulkUnfavorite] = useEdmDocumentationBulkUnfavoriteMutation({
    onError: (error) => showGraphQLErrors(error, t),
  });

  const onToggleFavorite = useCallback(
    (item: Documentation) => {
      const isFavorited = item.favorited;
      const mutation = isFavorited ? bulkUnfavorite : bulkFavorite;
      const successMessage = isFavorited
        ? t('remove-from-favorites-success')
        : t('add-to-favorites-success');
      mutation({
        variables: { ids: [item.id] },
        update: (cache) => {
          cache.modify({
            id: cache.identify(item),
            fields: {
              favorited: () => !isFavorited,
            },
          });
        },
        onCompleted: () => {
          toast({ content: successMessage });
        },
      });
    },
    [bulkFavorite, bulkUnfavorite, t],
  );

  const onDetails = useCallback(
    (item: Documentation) => {
      navigate(
        APP_URLS.app.documentation.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    },
    [navigate],
  );

  return { onToggleFavorite, onDetails };
}
